import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const STATUS_AR = {
  pending: { label: 'جديدة', color: 'var(--text-faint)' },
  in_progress: { label: 'قيد العمل', color: 'var(--c-blue)' },
  waiting_approval: { label: 'بانتظار اعتماد', color: 'var(--c-warning)' },
  revision: { label: 'مراجعة', color: 'var(--c-coral)' },
  approved: { label: 'معتمدة', color: 'var(--c-blue-500)' },
  completed: { label: 'مكتملة', color: 'var(--c-success)' },
  cancelled: { label: 'ملغاة', color: 'var(--text-muted)' },
};

const MARK_CYCLE = ['pending', 'in_progress', 'completed'];

// أعمدة التعليم اليدوي تُضاف لمسارات المونتاج/الإنتاج فقط
const isMontage = (name) => /مونتاج|إنتاج/.test(name || '');

function stepState(status) {
  if (status === 'completed') return 'done';
  if (['in_progress', 'waiting_approval', 'approved', 'revision'].includes(status)) return 'active';
  return 'todo';
}

function StatusIcon({ state, onClick, title }) {
  const cls = state === 'done' ? 'report-icon-done' : state === 'active' ? 'report-icon-active' : 'report-icon-todo';
  const glyph = state === 'done' ? '✓' : state === 'active' ? '◔' : '✕';
  return (
    <span
      className={`report-icon ${cls}`}
      title={title || (state === 'done' ? 'تم الإنجاز' : state === 'active' ? 'قيد العمل' : 'لم يبدأ')}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >{glyph}</span>
  );
}

export default function MonthlyReport({ month: monthProp, printable = false }) {
  const params = useParams();
  const month = monthProp || params.month;
  const { api } = useAuth();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api(`/reports/monthly?month=${month}`)
      .then(d => {
        setData(d);
        const map = { general: d.general_note || '' };
        (d.blocks || []).forEach(b => { map[b.client.id] = b.note || ''; });
        setNotes(map);
      })
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [api, month, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (printable && data) {
      const t = setTimeout(() => window.print(), 600);
      return () => clearTimeout(t);
    }
  }, [printable, data]);

  const saveNote = async (clientId) => {
    try {
      await api('/reports/monthly/note', {
        method: 'PUT',
        body: JSON.stringify({ month, client_id: clientId === 'general' ? null : clientId, body: notes[clientId] || '' }),
      });
      toast.success('تم حفظ التنبيه');
    } catch (err) { toast.error(err.message); }
  };

  const cycleMark = async (clientId, taskId, field, current) => {
    const next = MARK_CYCLE[(MARK_CYCLE.indexOf(current) + 1) % MARK_CYCLE.length];
    setData(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => b.client.id !== clientId ? b : {
        ...b,
        workflows: b.workflows.map(w => ({
          ...w,
          rows: w.rows.map(r => r.id === taskId ? { ...r, [field]: next } : r),
        })),
      }),
    }));
    try {
      await api(`/reports/monthly/task-mark/${taskId}`, { method: 'PUT', body: JSON.stringify({ [field]: next }) });
    } catch (err) { toast.error(err.message); }
  };

  if (loading && !data) return <div className="loader">جاري تحميل التقرير الشهري...</div>;
  if (!data) return <div className="empty">لا توجد بيانات</div>;

  const statusData = Object.entries(data.status_totals || {})
    .map(([k, v]) => ({ name: STATUS_AR[k]?.label || k, value: v, color: STATUS_AR[k]?.color || 'var(--text-faint)' }));

  const clientBar = data.blocks
    .filter(b => b.stats.total > 0)
    .map(b => ({ name: b.client.name, مكتملة: b.stats.completed, 'قيد العمل': b.stats.in_progress }));

  const editable = !printable;

  return (
    <div className={printable ? 'public-report report-mode' : 'report-mode'}>
      <header className="report-cover" style={!printable ? { borderRadius: 10, overflow: 'hidden', marginBottom: 16 } : undefined}>
        <div className="report-cover-inner">
          <div className="report-cover-text">
            <div className="report-cover-kicker">التقرير الشهري للأعمال</div>
            <h1 className="report-cover-title">منصة العمليات</h1>
            <div className="report-cover-period">{data.label}</div>
          </div>
        </div>
      </header>

      <main className={printable ? 'public-main' : undefined}>
        {printable && (
          <button className="btn btn-primary btn-sm no-print" style={{ marginBottom: 12 }} onClick={() => window.print()}>
            🖨 طباعة / حفظ PDF
          </button>
        )}

        <div className="public-stats-row" style={{ marginBottom: 18 }}>
          <div className="public-stat-card"><div className="public-stat-value">{data.totals.tasks}</div><div className="public-stat-label">إجمالي المهام</div></div>
          <div className="public-stat-card"><div className="public-stat-value">{data.totals.completed}</div><div className="public-stat-label">مكتملة</div></div>
          <div className="public-stat-card"><div className="public-stat-value">{data.totals.in_progress}</div><div className="public-stat-label">قيد العمل</div></div>
          <div className="public-stat-card"><div className="public-stat-value">{data.totals.clients}</div><div className="public-stat-label">العملاء</div></div>
        </div>

        {(statusData.length > 0 || clientBar.length > 0) && (
          <div className="report-section" style={{ padding: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
              {statusData.length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 8, textAlign: 'center' }}>توزيع حالات المهام</div>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                        {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
              {clientBar.length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 8, textAlign: 'center' }}>المهام حسب العميل</div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={clientBar}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="مكتملة" stackId="a" fill="var(--c-success)" />
                      <Bar dataKey="قيد العمل" stackId="a" fill="var(--c-blue)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        )}

        {/* تنبيه عام للشهر */}
        <NoteBox
          title="تنبيهات عامة للشهر"
          value={notes.general || ''}
          editable={editable}
          onChange={v => setNotes(n => ({ ...n, general: v }))}
          onSave={() => saveNote('general')}
        />

        {data.blocks.filter(b => b.stats.total > 0 || (notes[b.client.id] || '').trim()).map(b => (
          <section key={b.client.id} className="report-section">
            <div className="report-section-header">
              <h3>أعمال: {b.client.name}</h3>
              <div className="report-section-stats">
                <span>المجموع: <strong>{b.stats.total}</strong></span>
                <span>مكتملة: <strong>{b.stats.completed}</strong></span>
                <span>قيد العمل: <strong>{b.stats.in_progress}</strong></span>
              </div>
            </div>

            {b.stats.total === 0 && <div className="report-empty">لا توجد مهام في هذه الفترة</div>}

            {b.workflows.map(group => {
              const montage = isMontage(group.name);
              return (
                <div key={group.name} className="report-table-wrap">
                  <div className="report-table-title">{group.name}</div>
                  <table className="report-matrix">
                    <thead>
                      <tr>
                        <th className="report-matrix-rowhead">المهمة</th>
                        <th>الإنجاز</th>
                        {montage && <th>التصوير</th>}
                        {montage && <th>البث المباشر</th>}
                        {group.steps.map(s => <th key={s.order}>{s.name}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows.map(t => {
                        const byStep = {};
                        (t.subtasks || []).forEach(st => { byStep[st.current_step] = st.status; });
                        return (
                          <tr key={t.id}>
                            <td className="report-matrix-rowhead">{t.title}</td>
                            <td className="report-matrix-cell" title={t.last_completed_step ? `آخر خطوة مكتملة: ${t.last_completed_step}` : undefined}>
                              {t.progress_percent ?? 0}%
                              {t.last_completed_step && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{t.last_completed_step}</div>}
                            </td>
                            {montage && (
                              <td className="report-matrix-cell">
                                <StatusIcon
                                  state={stepState(t.filming_status)}
                                  onClick={editable ? () => cycleMark(b.client.id, t.id, 'filming_status', t.filming_status) : undefined}
                                  title={editable ? 'اضغط لتغيير حالة التصوير' : undefined}
                                />
                              </td>
                            )}
                            {montage && (
                              <td className="report-matrix-cell">
                                <StatusIcon
                                  state={stepState(t.broadcast_status)}
                                  onClick={editable ? () => cycleMark(b.client.id, t.id, 'broadcast_status', t.broadcast_status) : undefined}
                                  title={editable ? 'اضغط لتغيير حالة البث المباشر' : undefined}
                                />
                              </td>
                            )}
                            {group.steps.map(s => (
                              <td key={s.order} className="report-matrix-cell">
                                <StatusIcon state={stepState(byStep[s.order])} />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}

            {b.plain.length > 0 && (
              <ul className="report-task-list">
                {b.plain.map(t => (
                  <li key={t.id} className="report-task-item">
                    <span className="report-task-title">{t.title}</span>
                    <span className="report-task-state">
                      <StatusIcon state={stepState(t.status)} />
                      <span className="report-task-label">{STATUS_AR[t.status]?.label || t.status}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <NoteBox
              title={`تنبيهات خاصة بـ ${b.client.name}`}
              value={notes[b.client.id] || ''}
              editable={editable}
              onChange={v => setNotes(n => ({ ...n, [b.client.id]: v }))}
              onSave={() => saveNote(b.client.id)}
            />
          </section>
        ))}
      </main>

      <footer className="report-footer">
        <span>منصة العمليات</span>
        <span>التقرير الشهري — {data.label}</span>
      </footer>
    </div>
  );
}

function NoteBox({ title, value, editable, onChange, onSave }) {
  if (!editable && !value.trim()) return null;
  return (
    <div style={{ background: 'var(--c-gold-soft)', border: '1px solid var(--c-gold)', borderRadius: 8, padding: 14, margin: '0 0 18px' }}>
      <div style={{ fontWeight: 700, color: 'var(--c-gold-ink)', marginBottom: 8 }}>⚠️ {title}</div>
      {editable ? (
        <>
          <textarea
            className="form-input"
            rows={3}
            placeholder="اكتب التنبيهات والملاحظات التي ستظهر في تقرير الشهر..."
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{ width: '100%', resize: 'vertical' }}
          />
          <button className="btn btn-sm btn-outline no-print" style={{ marginTop: 8 }} onClick={onSave}>💾 حفظ التنبيه</button>
        </>
      ) : (
        <div style={{ whiteSpace: 'pre-wrap', color: 'var(--c-gold-ink)' }}>{value}</div>
      )}
    </div>
  );
}
